<script setup lang="ts">
import { computed, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import brandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { userModule } from '@/store/user/module'
import { screenModule } from '@/store/screen/module'
import { tutorModule } from '@/store/tutor/module'

const {
    Icon,
    Button: PocketButton,
    Tooltip,
} = UIKit

const emit = defineEmits<{
    toggleTutorChat: []
    selectedTutorExplanationPrompt: [message: string]
}>()

const showChatIconTooltip = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const isTutorResponding = computed(() => {
    return tutorModule.state.isTutorResponding
})
</script>

<template>
    <div
        class="tutor-explanation-prompts"
        role="group"
        aria-label="Tutor explanation prompt options"
    >
        <PocketButton
            class="tutor-explanation-prompts__tutor-chat-icon-btn"
            aria-label="Open tutor chat"
            :isDarkMode="isDarkMode"
            :type="'outline-grey'"
            :disabled="isTutorResponding"
            @focus="showChatIconTooltip = true"
            @blur="showChatIconTooltip = false"
            @mouseenter="showChatIconTooltip = true"
            @mouseleave="showChatIconTooltip = false"
            @click="emit('toggleTutorChat')"
        >
            <Icon class="tutor-explanation-prompts__tutor-icon" type="tutorChat" />
            <Tooltip
                v-if="showChatIconTooltip && breakpoint !== 'black-bear'"
                class="tutor-explanation-prompts__open-chat-tooltip"
                :key="'minimize'"
                :is-dark-mode="isDarkMode"
                :styles="{
                    'backgroundColor': isDarkMode ? brandColors.fog : brandColors.moonlitOcean,
                    'color': isDarkMode ? brandColors.brandBlack : brandColors.barelyBackground,
                }"
            >
                Ask something else
            </Tooltip>
        </PocketButton>
        <PocketButton
            class="tutor-explanation-prompts__still-have-questions-btn"
            :isDarkMode="isDarkMode"
            :type="'outline-grey'"
            :disabled="isTutorResponding"
            @click="emit(
                'selectedTutorExplanationPrompt',
                'I still have questions'
            )"
        >
            I still have questions
        </PocketButton>
        <PocketButton
            class="tutor-explanation-prompts__go-deeper-btn"
            aria-label="Go deeper into the explanation for this question"
            :isDarkMode="isDarkMode"
            :type="'outline-grey'"
            :disabled="isTutorResponding"
            @click="emit(
                'selectedTutorExplanationPrompt',
                'Go deeper into the explanation for this question'
            )"
        >
            Go deeper
        </PocketButton>
        <PocketButton
            class="tutor-explanation-prompts__simplify-btn"
            aria-label="Simplify the explanation for this question"
            :isDarkMode="isDarkMode"
            :type="'outline-grey'"
            :disabled="isTutorResponding"
            @click="emit(
                'selectedTutorExplanationPrompt',
                'Simplify the explanation for this question'
            )"
        >
            Simplify
        </PocketButton>
    </div>
</template>

<style lang="scss" scoped>
.tutor-explanation-prompts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 24px 0;

    :deep(button) {
        white-space: nowrap;
    }

    &__tutor-icon {
        width: 23px;
        height: 21px;
        padding-top: 6px;
    }

    &__open-chat-tooltip {
        position: absolute;
        top: -34px;
        left: 18px;
    }
}
</style>
